/**
 * API client.
 *
 * Handles bearer auth and transparent refresh: a 401 triggers one refresh
 * attempt, and concurrent 401s share that single attempt rather than each firing
 * their own rotation (which the server would treat as token replay and punish by
 * revoking every session).
 */

const BASE = '/api/v1';

const ACCESS_KEY = 'mcos.access';
const REFRESH_KEY = 'mcos.refresh';

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Turn a validation error into per-field messages.
 *
 * The API already says exactly which field failed and why, as
 * `details: [{ path, message }]`. Throwing that away and showing only "The
 * request body is invalid" leaves someone staring at a form with no idea which
 * of a dozen fields is wrong, which is what this exists to prevent.
 *
 * Returns an empty object for anything that is not a field-level validation
 * error, so callers can fall back to the message.
 */
export function fieldErrorsFrom(error: unknown): Record<string, string> {
  if (!(error instanceof ApiClientError)) return {};
  if (!Array.isArray(error.details)) return {};

  const fields: Record<string, string> = {};
  for (const issue of error.details) {
    if (!issue || typeof issue !== 'object') continue;
    const { path, message } = issue as { path?: unknown; message?: unknown };
    if (typeof path !== 'string' || typeof message !== 'string') continue;
    // First message per field: later ones are usually consequences of the first.
    if (!(path in fields)) fields[path] = message;
  }
  return fields;
}

/* ------------------------------------------------------------------ *
 * Token storage
 * ------------------------------------------------------------------ */

export const tokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/** Notified when refresh fails, so the app can bounce to the login screen. */
type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

function announceSessionExpired(): void {
  tokens.clear();
  for (const listener of sessionExpiredListeners) listener();
}

/* ------------------------------------------------------------------ *
 * Refresh coordination
 * ------------------------------------------------------------------ */

let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  // Every caller awaits the same promise, so only one rotation happens.
  refreshInFlight ??= (async () => {
    const refreshToken = tokens.refresh;
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = (await response.json()) as {
        accessToken: string;
        refreshToken: string;
      };
      tokens.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Release the lock so a later 401 can refresh again.
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/* ------------------------------------------------------------------ *
 * Request
 * ------------------------------------------------------------------ */

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the auth header (login, refresh). */
  anonymous?: boolean;
  /** Return the raw Response rather than parsed JSON (PDF downloads). */
  raw?: boolean;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers = new Headers(options.headers);

  const isFormData = options.body instanceof FormData;
  if (options.body !== undefined && !isFormData && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  if (!options.anonymous) {
    const access = tokens.access;
    if (access) headers.set('authorization', `Bearer ${access}`);
  }

  return fetch(`${BASE}${path}`, {
    ...options,
    headers,
    body:
      options.body === undefined
        ? undefined
        : isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body),
  });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options);

  if (response.status === 401 && !options.anonymous) {
    if (await refreshAccessToken()) {
      response = await send(path, options);
    } else {
      announceSessionExpired();
      throw new ApiClientError(401, 'UNAUTHORIZED', 'Your session has expired. Please sign in again.');
    }
  }

  if (options.raw) {
    if (!response.ok) throw await toError(response);
    return response as unknown as T;
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) throw await toError(response);

  return (await response.json()) as T;
}

async function toError(response: Response): Promise<ApiClientError> {
  let code = 'UNKNOWN';
  let message = `Request failed with status ${response.status}`;
  let details: unknown;

  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    code = body.error?.code ?? code;
    message = body.error?.message ?? message;
    details = body.error?.details;
  } catch {
    // Non-JSON error body; keep the generic message.
  }

  return new ApiClientError(response.status, code, message, details);
}

/* ------------------------------------------------------------------ *
 * Verbs
 * ------------------------------------------------------------------ */

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  /** Download a PDF as a blob, honouring auth and refresh. */
  async downloadPdf(path: string): Promise<Blob> {
    const response = await request<Response>(path, { method: 'GET', raw: true });
    return response.blob();
  },
};

/** Build a querystring, omitting empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}
